import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { toast } from 'react-toastify';
import PaymentHistory from '../components/PaymentHistory';
import Footer from '../components/Footer';

const Membership = () => {
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    loadMembershipStatus();
  }, []);

  const loadMembershipStatus = async () => {
    try {
      const response = await api.get('/membership/status');
      setMembershipStatus(response.data);
    } catch {
      toast.error('Error loading membership status');
    } finally {
      setLoading(false);
    }
  };

  const purchaseListingPack = async (planType) => {
    setActivating(true);
    setSelectedPlan(planType);

    try {
      // Use dev mode only if Razorpay is not configured
      if (process.env.NODE_ENV === 'development' && !membershipStatus?.razorpayConfigured) {
        await api.post('/membership/dev-activate', { planType });
        toast.success(`Development: ${planType} plan activated successfully!`);
        loadMembershipStatus();
        setActivating(false);
        setSelectedPlan(null);
        return;
      }

      const orderResponse = await api.post('/membership/create-order', { planType });
      const orderData = orderResponse.data;

      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: orderData.name,
        description: orderData.description,
        order_id: orderData.orderId,
        prefill: orderData.prefill,
        theme: orderData.theme,
        handler: async (response) => {
          try {

            // Validate that we have all required fields
            if (!response.razorpay_order_id || !response.razorpay_payment_id || !response.razorpay_signature) {
              throw new Error('Incomplete payment response from Razorpay');
            }

            // Prepare verification data
            const verificationData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planType: planType
            };

            const verifyResponse = await api.post('/membership/verify-payment', verificationData);
            toast.success(`${planType} plan activated successfully!`);
            loadMembershipStatus();
          } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Payment verification failed';
            toast.error(`Payment verification failed: ${errorMessage}`);
          } finally {
            setActivating(false);
          }
        },
        modal: {
          ondismiss: () => {
            setActivating(false);
          }
        }
      };

      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        throw new Error('Razorpay SDK not loaded. Please refresh the page and try again.');
      }

      new window.Razorpay(options).open();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Payment failed to initialize';
      toast.error(`Payment failed: ${errorMessage}`);
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F1EB]">
        <p className="text-lg font-semibold text-[#222831]">Loading membership…</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Membership Plans - VestrioZ</title>
        <meta
          name="description"
          content="Upgrade to VestrioZ premium membership for unlimited listings and exclusive benefits."
        />
        <meta name="keywords" content="membership, premium, listing packs, VestrioZ plans" />
      </Helmet>
      <div className="min-h-screen bg-[#F6F1EB]">
        <div className="max-w-5xl mx-auto px-4 py-10 space-y-12">

          {/* HEADER */}
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#222831] flex items-center justify-center mb-4 shadow-lg">
              <i className="fas fa-crown text-white text-2xl"></i>
            </div>
            <h1 className="text-4xl font-bold text-[#222831]">Premium Listing Packs</h1>
            <p className="text-[#393E46] mt-2">Grow faster with extra listing credits</p>
          </div>

          {/* STATUS */}
          <div className="bg-white rounded-2xl shadow-xl border border-[#E0D6C8] p-6">
            <h2 className="text-xl font-bold text-[#222831] mb-6">Your Status</h2>

            <div className="grid md:grid-cols-3 gap-5">
              {[
                {
                  title: 'Free Listings',
                  value: `${3 - (membershipStatus?.freeListingsUsed || 0)}/3`,
                  icon: 'fa-gift'
                },
                {
                  title: 'Premium Credits',
                  value: membershipStatus?.membershipListings || 0,
                  icon: 'fa-star'
                },
                {
                  title: 'Total Available',
                  value:
                    (3 - (membershipStatus?.freeListingsUsed || 0)) +
                    (membershipStatus?.membershipListings || 0),
                  icon: 'fa-layer-group'
                }
              ].map((card, i) => (
                <div
                  key={i}
                  className="p-5 rounded-xl border border-[#E0D6C8] shadow hover:shadow-lg transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#222831] flex items-center justify-center text-white">
                      <i className={`fas ${card.icon}`}></i>
                    </div>
                    <div>
                      <p className="text-sm text-[#393E46]">{card.title}</p>
                      <p className="text-2xl font-bold text-[#222831]">{card.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Plans */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* FREE Plan */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 p-6 relative flex flex-col">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-green-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                  INCLUDED
                </span>
              </div>

              {/* Header Section - Fixed Height */}
              <div className="text-center pt-4 h-32 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>FREE</h3>
                <p className="text-sm" style={{ color: '#393E46' }}>Perfect for getting started</p>
              </div>

              {/* Price Section - Fixed Height */}
              <div className="text-center h-20 flex flex-col justify-center mb-4">
                <div>
                  <span className="text-4xl font-bold" style={{ color: '#222831' }}>₹0</span>
                  <span className="text-sm" style={{ color: '#393E46' }}>/forever</span>
                </div>
              </div>

              {/* Features Section - Fixed Height */}
              <div className="h-48 space-y-3 mb-6">
                <div className="flex items-center">
                  <i className="fas fa-check text-green-500 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>3 Free Listings</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check text-green-500 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>30 Days Validity Each</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check text-green-500 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Basic Support</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check text-green-500 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Standard Visibility</span>
                </div>
                <div className="flex items-center opacity-30">
                  <i className="fas fa-times text-gray-400 w-5 mr-3"></i>
                  <span className="text-sm text-gray-400">Priority Support</span>
                </div>
                <div className="flex items-center opacity-30">
                  <i className="fas fa-times text-gray-400 w-5 mr-3"></i>
                  <span className="text-sm text-gray-400">Enhanced Visibility</span>
                </div>
              </div>

              {/* Button Section - Fixed Height */}
              <div className="h-12 flex items-end">
                <button
                  disabled
                  className="w-full py-3 rounded-xl font-bold text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
                >
                  Already Included
                </button>
              </div>
            </div>

            {/* BASIC Plan - New ₹11 Package */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-200 p-6 relative flex flex-col">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-xs font-bold">
                  AFFORDABLE
                </span>
              </div>

              {/* Header Section - Fixed Height */}
              <div className="text-center pt-4 h-32 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>BASIC</h3>
                <p className="text-sm" style={{ color: '#393E46' }}>Perfect for trying premium</p>
              </div>

              {/* Price Section - Fixed Height */}
              <div className="text-center h-20 flex flex-col justify-center mb-4">
                <div>
                  <span className="text-4xl font-bold" style={{ color: '#222831' }}>₹11</span>
                  <span className="text-sm" style={{ color: '#393E46' }}>/one-time</span>
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  Just ₹11 for 1 listing
                </div>
              </div>

              {/* Features Section - Fixed Height */}
              <div className="h-48 space-y-3 mb-6">
                <div className="flex items-center">
                  <i className="fas fa-check text-blue-500 w-5 mr-3"></i>
                  <span className="text-sm font-medium" style={{ color: '#222831' }}>1 Premium Listing</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check text-blue-500 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>30 Days Validity</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check text-blue-500 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Priority Support</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check text-blue-500 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Enhanced Visibility</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-infinity text-blue-500 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Credits Never Expire</span>
                </div>
                <div className="flex items-center opacity-30">
                  <i className="fas fa-times text-gray-400 w-5 mr-3"></i>
                  <span className="text-sm text-gray-400">Priority Listing Display</span>
                </div>
              </div>

              {/* Button Section - Fixed Height */}
              <div className="h-12 flex items-end">
                <button
                  onClick={() => purchaseListingPack('basic')}
                  disabled={activating && selectedPlan === 'basic'}
                  className="w-full py-3 rounded-xl font-bold text-sm text-white bg-blue-500 hover:bg-blue-600 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {activating && selectedPlan === 'basic' ? (
                    <div className="flex items-center justify-center">
                      <i className="fas fa-circle-notch fa-spin mr-2"></i>
                      Processing...
                    </div>
                  ) : (
                    'Buy Basic Pack'
                  )}
                </button>
              </div>
            </div>

            {/* STARTER Plan */}
            <div className="bg-white rounded-2xl shadow-xl border-2 p-6 relative flex flex-col" style={{ borderColor: '#948979' }}>
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                <span className="text-white px-4 py-1 rounded-full text-xs font-bold whitespace-nowrap" style={{ backgroundColor: '#948979' }}>
                  MOST POPULAR
                </span>
              </div>

              {/* Header Section - Fixed Height */}
              <div className="text-center pt-4 h-32 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>STARTER</h3>
                <p className="text-sm" style={{ color: '#393E46' }}>Ideal for growing businesses</p>
              </div>

              {/* Price Section - Fixed Height */}
              <div className="text-center h-20 flex flex-col justify-center mb-4">
                <div>
                  <span className="text-4xl font-bold" style={{ color: '#222831' }}>₹49</span>
                  <span className="text-sm" style={{ color: '#393E46' }}>/one-time</span>
                </div>
                <div className="text-xs mt-1" style={{ color: '#948979' }}>
                  Just ₹9.8 per listing
                </div>
              </div>

              {/* Features Section - Fixed Height */}
              <div className="h-48 space-y-3 mb-6">
                <div className="flex items-center">
                  <i className="fas fa-check w-5 mr-3" style={{ color: '#948979' }}></i>
                  <span className="text-sm font-medium" style={{ color: '#222831' }}>5 Premium Listings</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check w-5 mr-3" style={{ color: '#948979' }}></i>
                  <span className="text-sm" style={{ color: '#222831' }}>30 Days Validity Each</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check w-5 mr-3" style={{ color: '#948979' }}></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Priority Support</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check w-5 mr-3" style={{ color: '#948979' }}></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Enhanced Visibility</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-infinity w-5 mr-3" style={{ color: '#948979' }}></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Credits Never Expire</span>
                </div>
                <div className="flex items-center opacity-30">
                  <i className="fas fa-times text-gray-400 w-5 mr-3"></i>
                  <span className="text-sm text-gray-400">Priority Listing Display</span>
                </div>
              </div>

              {/* Button Section - Fixed Height */}
              <div className="h-12 flex items-end">
                <button
                  onClick={() => purchaseListingPack('starter')}
                  disabled={activating && selectedPlan === 'starter'}
                  className="w-full py-3 rounded-xl font-bold text-sm text-white shadow-lg hover:shadow-xl transition-all duration-300"
                  style={{ backgroundColor: '#948979' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                >
                  {activating && selectedPlan === 'starter' ? (
                    <div className="flex items-center justify-center">
                      <i className="fas fa-circle-notch fa-spin mr-2"></i>
                      Processing...
                    </div>
                  ) : (
                    'Buy Starter Pack'
                  )}
                </button>
              </div>
            </div>

            {/* PRO Plan */}
            <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 p-6 relative flex flex-col">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-xs font-bold">
                  BEST VALUE
                </span>
              </div>

              {/* Header Section - Fixed Height */}
              <div className="text-center pt-4 h-32 flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#222831' }}>PRO</h3>
                <p className="text-sm" style={{ color: '#393E46' }}>Perfect for established businesses</p>
              </div>

              {/* Price Section - Fixed Height */}
              <div className="text-center h-20 flex flex-col justify-center mb-4">
                <div>
                  <span className="text-4xl font-bold" style={{ color: '#222831' }}>₹99</span>
                  <span className="text-sm" style={{ color: '#393E46' }}>/one-time</span>
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  Just ₹9 per listing • Save ₹45
                </div>
              </div>

              {/* Features Section - Fixed Height */}
              <div className="h-48 space-y-3 mb-6">
                <div className="flex items-center">
                  <i className="fas fa-check text-purple-600 w-5 mr-3"></i>
                  <span className="text-sm font-medium" style={{ color: '#222831' }}>11 Premium Listings</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check text-purple-600 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>30 Days Validity Each</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check text-purple-600 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Premium Support</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-check text-purple-600 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Maximum Visibility</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-infinity text-purple-600 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Credits Never Expire</span>
                </div>
                <div className="flex items-center">
                  <i className="fas fa-star text-purple-600 w-5 mr-3"></i>
                  <span className="text-sm" style={{ color: '#222831' }}>Priority Listing Display</span>
                </div>
              </div>

              {/* Button Section - Fixed Height */}
              <div className="h-12 flex items-end">
                <button
                  onClick={() => purchaseListingPack('pro')}
                  disabled={activating && selectedPlan === 'pro'}
                  className="w-full py-3 rounded-xl font-bold text-sm text-white bg-purple-600 hover:bg-purple-700 shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {activating && selectedPlan === 'pro' ? (
                    <div className="flex items-center justify-center">
                      <i className="fas fa-circle-notch fa-spin mr-2"></i>
                      Processing...
                    </div>
                  ) : (
                    'Buy Pro Pack'
                  )}
                </button>
              </div>
            </div>
          </div>


          {/* FAQ */}
          <div className="bg-white rounded-2xl shadow-xl border border-[#E0D6C8] p-6">
            <h2 className="text-2xl font-bold text-center mb-6 text-[#222831]">
              FAQs
            </h2>

            <div className="space-y-3">
              {[
                {
                  q: 'Do credits expire?',
                  a: 'No, your listing credits never expire. Each individual listing stays active for 30 days, but unused credits remain in your account forever.'
                },
                {
                  q: 'How long do listings stay active?',
                  a: 'All listings (free and premium) stay active for 30 days from the date of posting. After 30 days, the listing expires and you need to use another credit to repost it.'
                },
                {
                  q: 'What are the current pricing plans?',
                  a: 'FREE: 3 listings (₹0), BASIC: 1 listing (₹11), STARTER: 5 listings (₹49 - ₹9.8 per listing), PRO: 11 listings (₹99 - ₹9 per listing). PRO offers the best value with maximum savings.'
                },
                {
                  q: 'Can I buy multiple packs?',
                  a: 'Yes! You can purchase multiple packs (BASIC, STARTER, and PRO). All credits stack up in your account and can be used anytime. Credits never expire.'
                },
                {
                  q: 'What\'s the difference between free and premium listings?',
                  a: 'Free listings get standard visibility. Premium listings get enhanced visibility, priority support, and better placement in search results. PRO plan also includes priority listing display.'
                },
                {
                  q: 'Is payment secure?',
                  a: 'Absolutely! All payments are processed through Razorpay with bank-level 256-bit SSL encryption. Your payment information is completely secure.'
                },
                {
                  q: 'Which plan should I choose?',
                  a: 'Start with FREE to test the platform. Choose BASIC (₹11) to try premium features. Choose STARTER (₹49) if you have 5-10 items. Choose PRO (₹99) for the best value if you have many items to rent.'
                },
                {
                  q: 'What happens when my listing expires?',
                  a: 'After 30 days, your listing becomes inactive. You can repost it using another credit from your account. If you have unused credits, you can create new listings anytime.'
                }
              ].map((faq, i) => (
                <div key={i} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    className="w-full p-4 flex justify-between items-center bg-[#F6F1EB]"
                  >
                    <span className="font-semibold text-[#222831]">{faq.q}</span>
                    <i
                      className={`fas fa-chevron-down transition ${expandedFaq === i ? 'rotate-180' : ''
                        }`}
                    ></i>
                  </button>

                  {expandedFaq === i && (
                    <div className="p-4 text-sm text-[#393E46] bg-white">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* PAYMENT HISTORY */}
          {membershipStatus?.paymentHistory?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-xl border border-[#E0D6C8] p-6">
              <PaymentHistory payments={membershipStatus.paymentHistory} />
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Membership;
